import { useEffect, useRef } from 'react';
import InteractiveWaveform from '@/components/feature/InteractiveWaveform';

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const delay = target.dataset.revealDelay || '0';
            target.style.animationDelay = `${delay}ms`;
            target.classList.add('animate-fade-in-up');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.2 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen w-full overflow-hidden bg-background-50"
    >
      {/* Grain texture overlay */}
      <div className="absolute inset-0 bg-grain opacity-40 pointer-events-none z-10" />

      {/* Radial spotlight */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_oklch(var(--primary-500)/0.06)_0%,_transparent_65%)] pointer-events-none z-[5]" />

      {/* Animated interactive waveform background */}
      <div className="absolute inset-0 z-[1] opacity-35">
        <InteractiveWaveform />
      </div>

      {/* Floating orbs for depth */}
      <div className="absolute top-1/4 left-[15%] w-64 h-64 rounded-full bg-primary-500/5 blur-3xl animate-float pointer-events-none z-[2]" />
      <div className="absolute bottom-1/3 right-[10%] w-80 h-80 rounded-full bg-accent-500/4 blur-3xl animate-pulse-glow pointer-events-none z-[2]" style={{ animationDelay: '1.5s' }} />

      {/* Content */}
      <div className="relative z-20 flex flex-col items-center justify-center min-h-screen px-6 md:px-12 lg:px-20 py-24 text-center">
        {/* Tag label */}
        <div className="reveal opacity-0 mb-6" data-reveal-delay="0">
          <span className="inline-flex items-center px-4 py-2 border border-primary-500/30 rounded-full text-xs font-body font-medium tracking-widest text-primary-500 uppercase bg-primary-500/5 backdrop-blur-sm">
            Clinical Diagnostics Vocal Biomarker Technology
          </span>
        </div>

        {/* Main headline */}
        <h1 className="reveal opacity-0 font-display text-4xl md:text-6xl lg:text-7xl font-medium text-foreground-50 leading-[1.1] mb-6 max-w-4xl" data-reveal-delay="150">
          Your Voice is a{' '}
          <span className="text-primary-500">Diagnostic Tool</span>
        </h1>

        {/* Subtitle */}
        <p className="reveal opacity-0 font-body text-base md:text-lg text-foreground-400 leading-relaxed max-w-2xl mb-10" data-reveal-delay="300">
          Voice production is a complex neurophysiological process involving over 120 muscles, multiple brain regions, and neurotransmitter systems. Subtle changes in pitch, rhythm, and articulation can signal neurological or mental health disorders years before traditional symptoms appear.
        </p>

        {/* CTA button */}
        <div className="reveal opacity-0" data-reveal-delay="450">
          <a
            href="#technology"
            className="inline-flex items-center gap-3 px-8 py-4 bg-primary-500 hover:bg-primary-600 text-background-50 font-body font-medium text-sm tracking-wide rounded-lg transition-all duration-500 ease-silk hover:scale-[1.02] group whitespace-nowrap"
          >
            <span>Explore Technology</span>
            <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center transition-transform duration-500 ease-silk group-hover:translate-x-1" />
          </a>
        </div>

        {/* Stats bar */}
        <div className="reveal opacity-0 mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 max-w-4xl mx-auto" data-reveal-delay="600">
          {[
            { value: '45s', label: 'Minimum sample duration' },
            { value: '91.61%', label: 'Average classification accuracy' },
            { value: '1.8B', label: 'AI model parameters' },
            { value: '102K+', label: 'Clinical sessions trained' },
          ].map((stat, i) => (
            <div key={i} className="text-center group cursor-default">
              <div className="font-display text-2xl md:text-3xl text-primary-500 mb-1 transition-transform duration-500 ease-silk group-hover:scale-110">
                {stat.value}
              </div>
              <div className="font-body text-xs text-foreground-600 leading-tight transition-colors duration-500 ease-silk group-hover:text-foreground-400">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent z-20 pointer-events-none" />
    </section>
  );
}